import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, errorMessage, tokens } from '@/lib/api';
import { Button, Card, Toast } from '@/components/ui';

interface Result {
  ok: boolean;
  logo_url: string;
  size_bytes: number;
}

/**
 * Logo upload.
 *
 * Uses `fetch` with a `FormData` body rather than the `api` helper: that helper
 * sets `Content-Type: application/json`, and a multipart body needs the browser
 * to set the header itself so it can include the boundary. Setting it by hand
 * produces a request the server cannot parse.
 */
export function LogoUpload({
  endpoint,
  currentUrl,
  label = 'Logo',
  description,
  invalidate,
}: {
  endpoint: string;
  currentUrl?: string | null;
  label?: string;
  description?: string;
  invalidate?: string[];
}) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(null);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.append('file', file);

      const response = await fetch(`/api/v1${endpoint}`, {
        method: 'POST',
        body,
        headers: {
          // Authorization only. Content-Type is left to the browser.
          ...(tokens.access() ? { Authorization: `Bearer ${tokens.access()}` } : {}),
        },
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new ApiError(
          response.status,
          payload?.error ?? 'upload_failed',
          payload?.message ?? payload?.detail ?? 'Upload failed',
        );
      }
      return payload as Result;
    },
    onSuccess: (result) => {
      setPreview(result.logo_url);
      for (const key of invalidate ?? []) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
      queryClient.invalidateQueries({ queryKey: ['branding'] });
      setToast({
        message: `Uploaded (${Math.round(result.size_bytes / 1024)}KB). Reload to see it in the shell.`,
        tone: 'good',
      });
    },
    onError: (err) =>
      setToast({
        message: errorMessage(err, 'Upload failed'),
        tone: 'bad',
      }),
  });

  const shown = preview ?? currentUrl ?? null;

  return (
    <>
      <Card title={label} description={description}>
        <div className="flex flex-wrap items-center gap-5">
          <div className="grid h-16 w-32 shrink-0 place-items-center rounded-lg border border-dashed border-line bg-sunken">
            {shown ? (
              <img
                src={shown}
                alt={label}
                className="max-h-14 max-w-28 object-contain"
              />
            ) : (
              <span className="text-2xs text-ink-400">No logo</span>
            )}
          </div>

          <div className="min-w-0">
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp,image/x-icon"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) upload.mutate(file);
                // Clear the input so picking the same file twice still fires.
                event.target.value = '';
              }}
            />
            <Button
              variant="secondary"
              loading={upload.isPending}
              onClick={() => inputRef.current?.click()}
            >
              {shown ? 'Replace' : 'Upload'} image
            </Button>
            <p className="mt-2 max-w-sm text-2xs leading-relaxed text-ink-500 dark:text-ink-400">
              PNG, JPEG, SVG, WebP or ICO, up to 2MB. An SVG containing script
              is rejected — it would run in this portal&apos;s own origin.
            </p>
          </div>
        </div>
      </Card>

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </>
  );
}
